if (!customElements.get('collection-filter-tabs')) {
  class CollectionFilterTabs extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToTabs) return;

      this.isConnectedToTabs = true;
      this.intersectionParam = 'filter_tabs_collection';
      this.intersectionPageParam = 'filter_tabs_page';
      this.tabs = Array.from(this.querySelectorAll('[data-filter-tab]'));
      this.filterTabs = this.tabs.filter((tab) => tab.dataset.filterAction === 'filter');
      this.intersectionTabs = this.tabs.filter((tab) => tab.dataset.filterAction === 'intersection');
      this.tabEntries = new Map();
      this.intersectionSourceCache = new Map();

      this.filterTabs.forEach((tab) => {
        if (tab.hasAttribute('data-filter-all')) {
          this.tabEntries.set(tab, []);
          return;
        }

        const url = new URL(tab.href, window.location.origin);
        const entries = Array.from(url.searchParams.entries())
          .filter(([key]) => key.startsWith('filter.'));
        this.tabEntries.set(tab, entries);
      });

      this.controlledKeys = new Set();
      this.tabEntries.forEach((entries) => {
        entries.forEach(([key]) => this.controlledKeys.add(key));
      });

      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.documentClickHandler = this.documentClickHandler || this.handleDocumentClick.bind(this);
      this.facetUpdateHandler = this.facetUpdateHandler || this.handleFacetUpdate.bind(this);
      this.popstateHandler = this.popstateHandler || this.handlePopstate.bind(this);

      this.addEventListener('click', this.clickHandler);
      document.addEventListener('click', this.documentClickHandler);
      document.addEventListener('on:facet-filters:updated', this.facetUpdateHandler);
      window.addEventListener('popstate', this.popstateHandler);

      this.applyNativeFilterVisibility();
      const params = new URLSearchParams(window.location.search);
      this.sync(params);
      this.moveSoldOutProductsToEnd();
      this.refreshIntersection(params, { render: Boolean(params.get(this.intersectionParam)) });
    }

    disconnectedCallback() {
      this.isConnectedToTabs = false;
      this.removeEventListener('click', this.clickHandler);
      document.removeEventListener('click', this.documentClickHandler);
      document.removeEventListener('on:facet-filters:updated', this.facetUpdateHandler);
      window.removeEventListener('popstate', this.popstateHandler);
      this.intersectionAbortController?.abort();
      this.standaloneAbortController?.abort();

      if (this.dataset.hideNativeFilters === 'true') {
        document.documentElement.classList.remove('collection-filter-tabs--hide-native');
      }
    }

    async handlePopstate() {
      window.setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        this.sync(params);
        if (params.get(this.intersectionParam)) {
          this.refreshIntersection(params, { render: true });
          return;
        }

        const facetFilters = document.querySelector('facet-filters');
        if ((!facetFilters || typeof facetFilters.applyFilters !== 'function') && this.filterTabs.length > 0) {
          this.applyStandaloneFilter(params, { updateUrl: false });
        }
      }, 0);
    }

    handleFacetUpdate() {
      const params = new URLSearchParams(window.location.search);
      this.sync(params);
      this.moveSoldOutProductsToEnd();
      this.refreshIntersection(params, { render: Boolean(params.get(this.intersectionParam)), force: true });
    }

    async handleClick(evt) {
      const tab = evt.target.closest('[data-filter-tab]');
      if (!tab || !this.contains(tab) || tab.dataset.filterAction === 'navigate') return;
      if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

      evt.preventDefault();

      const searchParams = this.buildSearchParams(tab);
      const targetUrl = this.buildUrl(searchParams);

      if (tab.dataset.filterAction === 'intersection') {
        window.history.pushState({ searchParams: searchParams.toString() }, '', targetUrl);
        this.sync(searchParams);
        await this.refreshIntersection(searchParams, { render: true });

        if (this.dataset.scrollToProducts === 'true') {
          this.scrollToProducts();
        }
        return;
      }

      const facetFilters = document.querySelector('facet-filters');
      this.intersectionAbortController?.abort();
      this.sync(searchParams);
      this.setLoading(true);

      const requestId = (this.requestId || 0) + 1;
      this.requestId = requestId;

      try {
        if (facetFilters && typeof facetFilters.applyFilters === 'function') {
          await facetFilters.applyFilters(searchParams.toString(), evt);
        } else {
          await this.applyStandaloneFilter(searchParams);
        }

        if (this.dataset.scrollToProducts === 'true') {
          this.scrollToProducts();
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('Unable to update collection filters.', error); // eslint-disable-line
      } finally {
        if (requestId === this.requestId) {
          this.setLoading(false);
          this.sync(new URLSearchParams(window.location.search));
        }
      }
    }

    async applyStandaloneFilter(searchParams, options = {}) {
      const results = document.getElementById('filter-results');
      if (!results) throw new Error('The collection product grid was not found.');

      const sectionId = results.dataset.sectionId;
      if (!sectionId) throw new Error('The collection product section ID was not found.');

      this.standaloneAbortController?.abort();
      this.standaloneAbortController = new AbortController();

      const requestParams = new URLSearchParams(searchParams);
      requestParams.delete('page');
      requestParams.set('section_id', sectionId);

      const response = await fetch(`${window.location.pathname}?${requestParams.toString()}`, {
        method: 'GET',
        signal: this.standaloneAbortController.signal
      });
      if (!response.ok) throw new Error(`Collection filter request failed (${response.status}).`);

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const fetchedResults = template.content.getElementById('filter-results');
      if (!fetchedResults) throw new Error('The filtered collection product grid was not returned.');

      const existingGridClass = results.querySelector('ul')?.getAttribute('class');
      results.innerHTML = fetchedResults.innerHTML;
      Array.from(fetchedResults.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-')) results.setAttribute(attribute.name, attribute.value);
      });

      const newGrid = results.querySelector('ul');
      if (newGrid && existingGridClass) newGrid.setAttribute('class', existingGridClass);

      const resultsContainer = results.closest('[data-num-results]');
      const fetchedContainer = fetchedResults.closest('[data-num-results]');
      if (resultsContainer && fetchedContainer) {
        resultsContainer.dataset.numResults = fetchedContainer.dataset.numResults || '0';
      }

      const customPagination = results.closest('custom-pagination');
      if (customPagination?.reload) customPagination.reload();

      if (options.updateUrl !== false) {
        window.history.pushState(
          { searchParams: searchParams.toString() },
          '',
          this.buildUrl(searchParams)
        );
      }

      this.sync(searchParams);
      this.moveSoldOutProductsToEnd();
      document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
    }

    scrollToProducts() {
      const results = document.getElementById('filter-results');
      if (!results) return;

      let headerHeight = 0;
      const stickyHeader = document.querySelector('store-header[data-is-sticky="true"]');
      if (stickyHeader) headerHeight = stickyHeader.getBoundingClientRect().height;

      window.scrollTo({
        top: results.getBoundingClientRect().top + window.scrollY - headerHeight - 16,
        behavior: 'smooth'
      });
    }

    moveSoldOutProductsToEnd() {
      if (this.dataset.soldOutLast !== 'true') return;

      const grid = document.querySelector('#filter-results > ul.grid');
      if (!grid) return;

      const soldOutItems = Array.from(grid.children).filter((item) => {
        const card = item.querySelector('product-card');
        if (!card) return false;

        if (card.querySelector('.product-label--sold-out')) return true;

        const addButton = card.querySelector('.card__quick-add button[name="add"]');
        if (addButton?.disabled) return true;

        return !card.querySelector('.card__quick-add button')
          && Boolean(card.querySelector('.price--sold-out'));
      });

      soldOutItems.forEach((item) => grid.appendChild(item));
    }

    handleDocumentClick(evt) {
      const pageLink = evt.target.closest('[data-intersection-page]');
      if (!pageLink) return;
      if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

      evt.preventDefault();
      const url = new URL(pageLink.href, window.location.origin);
      window.history.pushState({ searchParams: url.searchParams.toString() }, '', url);
      this.sync(url.searchParams);
      this.refreshIntersection(url.searchParams, { render: true, scroll: true });
    }

    buildSearchParams(tab, sourceParams = new URLSearchParams(window.location.search)) {
      const preserveFilters = this.dataset.preserveFilters === 'true';
      const params = preserveFilters
        ? new URLSearchParams(sourceParams)
        : new URLSearchParams();

      params.delete('page');
      params.delete('section_id');
      params.delete(this.intersectionParam);
      params.delete(this.intersectionPageParam);
      this.controlledKeys.forEach((key) => params.delete(key));

      if (tab.dataset.filterAction === 'intersection') {
        const handle = tab.dataset.filterCollectionHandle;
        if (handle) params.set(this.intersectionParam, handle);
      } else {
        const entries = this.tabEntries.get(tab) || [];
        entries.forEach(([key, value]) => params.append(key, value));
      }

      return params;
    }

    buildUrl(params) {
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    sync(params) {
      this.updateActiveState(params);
      this.updateFilterLinks(params);
      this.updateAvailability();
      this.syncIntersectionFormField(params);
      this.applyClosedGridLayout();
    }

    getControlledEntries(params) {
      const entries = [];
      this.controlledKeys.forEach((key) => {
        params.getAll(key).forEach((value) => entries.push(`${key}=${value}`));
      });
      return entries.sort();
    }

    updateActiveState(params) {
      const intersectionHandle = params.get(this.intersectionParam);
      const currentSignature = this.getControlledEntries(params).join('&');

      this.filterTabs.forEach((tab) => {
        const entries = (this.tabEntries.get(tab) || [])
          .map(([key, value]) => `${key}=${value}`)
          .sort();
        const isActive = !intersectionHandle && entries.join('&') === currentSignature;

        tab.classList.toggle('is-active', isActive);
        if (isActive) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });

      this.intersectionTabs.forEach((tab) => {
        const isActive = tab.dataset.filterCollectionHandle === intersectionHandle;
        tab.classList.toggle('is-active', isActive);
        if (isActive) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });

      this.tabs
        .filter((tab) => tab.dataset.filterAction === 'navigate')
        .forEach((tab) => {
          const tabUrl = new URL(tab.href, window.location.origin);
          const isActive = tabUrl.pathname === window.location.pathname;
          tab.classList.toggle('is-active', isActive);
          if (isActive) tab.setAttribute('aria-current', 'page');
          else tab.removeAttribute('aria-current');
        });
    }

    updateFilterLinks(params) {
      [...this.filterTabs, ...this.intersectionTabs].forEach((tab) => {
        tab.href = this.buildUrl(this.buildSearchParams(tab, params));
      });
    }

    updateAvailability() {
      const showCounts = this.dataset.showCounts === 'true';
      const hideUnavailable = this.dataset.hideUnavailable === 'true';
      if (!showCounts && !hideUnavailable) return;

      const form = document.getElementById('facets');
      const formControls = form ? Array.from(form.elements) : [];

      this.filterTabs.forEach((tab) => {
        if (tab.hasAttribute('data-filter-all')) return;

        const entries = this.tabEntries.get(tab) || [];
        const matchedControls = entries.map(([key, value]) => formControls.find((control) => (
          control.name === key && control.value === value
        )));
        const isAvailable = form
          ? entries.length > 0 && matchedControls.every((control) => (
            control && (!control.disabled || control.checked)
          ))
          : tab.dataset.filterAvailable === 'true';
        const isActive = tab.hasAttribute('aria-current');

        const item = tab.closest('.filter-tabs__item');
        if (item) item.hidden = hideUnavailable && !isAvailable && !isActive;

        const count = tab.querySelector('[data-tab-count]');
        if (count && showCounts) {
          const control = matchedControls.length === 1 ? matchedControls[0] : null;
          const sourceCount = control?.nextElementSibling?.querySelector('.filter__count');

          if (sourceCount) {
            count.textContent = sourceCount.textContent;
            count.hidden = false;
          } else if (control?.disabled) {
            count.textContent = '(0)';
            count.hidden = false;
          } else if (form && entries.length > 0) {
            count.textContent = '(0)';
            count.hidden = false;
          } else if (typeof tab.dataset.filterCount !== 'undefined') {
            count.textContent = `(${tab.dataset.filterCount})`;
            count.hidden = false;
          } else {
            count.hidden = true;
          }
        }
      });
    }

    syncIntersectionFormField(params) {
      const form = document.getElementById('facets');
      if (!form) return;

      form.querySelectorAll('[data-filter-tabs-intersection-input]').forEach((input) => input.remove());
      const handle = params.get(this.intersectionParam);
      if (!handle) return;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = this.intersectionParam;
      input.value = handle;
      input.dataset.filterTabsIntersectionInput = '';
      form.appendChild(input);
    }

    getNativeParams(params) {
      const nativeParams = new URLSearchParams(params);
      nativeParams.delete('page');
      nativeParams.delete('section_id');
      nativeParams.delete(this.intersectionParam);
      nativeParams.delete(this.intersectionPageParam);
      nativeParams.sort();
      return nativeParams;
    }

    async refreshIntersection(params, options = {}) {
      if (this.intersectionTabs.length === 0) return;

      const handle = params.get(this.intersectionParam);
      const shouldLoad = options.render
        || this.dataset.showCounts === 'true'
        || this.dataset.hideUnavailable === 'true';
      if (!shouldLoad) return;

      const requestId = (this.intersectionRequestId || 0) + 1;
      this.intersectionRequestId = requestId;
      if (options.render) this.setLoading(true);

      try {
        const source = await this.loadIntersectionSource(params, options.force);
        if (requestId !== this.intersectionRequestId) return;

        this.updateIntersectionCounts(source.products);
        if (options.render && handle) {
          this.renderIntersection(source, handle, params);
          if (options.scroll) {
            document.getElementById('filter-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Unable to compare linked collections.', error); // eslint-disable-line
        }
      } finally {
        if (requestId === this.intersectionRequestId && options.render) this.setLoading(false);
      }
    }

    async loadIntersectionSource(params, force = false) {
      const nativeParams = this.getNativeParams(params);
      const cacheKey = nativeParams.toString();
      if (!force && this.intersectionSourceCache.has(cacheKey)) {
        return this.intersectionSourceCache.get(cacheKey);
      }

      this.intersectionAbortController?.abort();
      this.intersectionAbortController = new AbortController();
      const signal = this.intersectionAbortController.signal;
      const sourcePromise = this.fetchAllProductPages(nativeParams, signal);
      this.intersectionSourceCache.set(cacheKey, sourcePromise);

      try {
        return await sourcePromise;
      } catch (error) {
        this.intersectionSourceCache.delete(cacheKey);
        throw error;
      }
    }

    async fetchAllProductPages(nativeParams, signal) {
      const firstPage = await this.fetchProductPage(nativeParams, 1, signal);
      const pages = [firstPage];
      const remainingPages = Array.from(
        { length: Math.max(0, firstPage.totalPages - 1) },
        (_, index) => index + 2
      );

      for (let index = 0; index < remainingPages.length; index += 4) {
        const batch = remainingPages.slice(index, index + 4);
        const responses = await Promise.all(
          batch.map((page) => this.fetchProductPage(nativeParams, page, signal))
        );
        pages.push(...responses);
      }

      const uniqueProducts = new Map();
      pages.forEach((page) => {
        page.products.forEach((product) => {
          if (!uniqueProducts.has(product.id)) uniqueProducts.set(product.id, product);
        });
      });

      return {
        products: Array.from(uniqueProducts.values()),
        pageSize: firstPage.pageSize,
        gridTemplate: firstPage.gridTemplate
      };
    }

    async fetchProductPage(nativeParams, page, signal) {
      const params = new URLSearchParams(nativeParams);
      if (page > 1) params.set('page', page);

      const results = document.getElementById('filter-results');
      const sectionId = results?.dataset.sectionId
        || document.getElementById('facets')?.dataset.filterSectionId;
      if (sectionId) params.set('section_id', sectionId);

      const response = await fetch(this.buildUrl(params), { method: 'GET', signal });
      if (!response.ok) throw new Error(`Collection page request failed (${response.status}).`);

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const fetchedResults = template.content.getElementById('filter-results');
      if (!fetchedResults) throw new Error('The collection product grid was not found.');

      const fetchedGrid = fetchedResults.querySelector('ul.grid');
      const products = Array.from(fetchedResults.querySelectorAll('[data-intersection-product]')).map((item) => ({
        id: item.dataset.productId,
        html: item.outerHTML,
        collections: new Set((item.dataset.productCollections || '').split(/\s+/).filter(Boolean))
      }));

      return {
        products,
        totalPages: Number(fetchedResults.dataset.totalPages || 1),
        pageSize: Number(fetchedResults.dataset.pageSize || results?.dataset.pageSize || 16),
        gridTemplate: fetchedGrid ? fetchedGrid.cloneNode(false) : null
      };
    }

    updateIntersectionCounts(products) {
      const showCounts = this.dataset.showCounts === 'true';
      const hideUnavailable = this.dataset.hideUnavailable === 'true';

      this.intersectionTabs.forEach((tab) => {
        const handle = tab.dataset.filterCollectionHandle;
        const countValue = products.reduce(
          (count, product) => count + (product.collections.has(handle) ? 1 : 0),
          0
        );
        const count = tab.querySelector('[data-tab-count]');
        const item = tab.closest('.filter-tabs__item');

        if (count && showCounts) {
          count.textContent = `(${countValue})`;
          count.hidden = false;
        }
        if (item) item.hidden = hideUnavailable && countValue === 0 && !tab.hasAttribute('aria-current');
      });
    }

    renderIntersection(source, handle, params) {
      const results = document.getElementById('filter-results');
      if (!results) return;

      const customPagination = results.closest('custom-pagination');
      if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

      const matchingProducts = source.products.filter((product) => product.collections.has(handle));
      const pageSize = Math.max(1, source.pageSize || 16);
      const totalPages = Math.max(1, Math.ceil(matchingProducts.length / pageSize));
      const requestedPage = Number(params.get(this.intersectionPageParam) || 1);
      const currentPage = Math.min(Math.max(1, requestedPage), totalPages);
      const start = (currentPage - 1) * pageSize;
      const visibleProducts = matchingProducts.slice(start, start + pageSize);

      if (visibleProducts.length === 0) {
        results.innerHTML = `<p>${this.escapeHtml(this.dataset.emptyMessage || 'No products found')}</p>`;
      } else {
        const grid = source.gridTemplate
          ? source.gridTemplate.cloneNode(false)
          : document.createElement('ul');
        if (!grid.classList.contains('grid')) grid.className = 'grid mb-8 md:mb-12 gap-x-theme gap-y-16';
        grid.setAttribute('role', 'list');
        grid.innerHTML = visibleProducts.map((product) => product.html).join('');
        results.replaceChildren(grid);

        if (totalPages > 1) {
          results.insertAdjacentHTML('beforeend', this.buildIntersectionPagination(currentPage, totalPages, params));
        }
      }

      results.dataset.intersectionHandle = handle;
      results.dataset.intersectionCount = matchingProducts.length;
      const resultsContainer = results.closest('[data-num-results]');
      if (resultsContainer) resultsContainer.dataset.numResults = matchingProducts.length;
      if (customPagination?.reload) customPagination.reload();
      this.moveSoldOutProductsToEnd();
      this.applyClosedGridLayout();
    }

    buildIntersectionPagination(currentPage, totalPages, params) {
      const pageNumbers = new Set([1, totalPages]);
      for (let page = Math.max(1, currentPage - 2); page <= Math.min(totalPages, currentPage + 2); page += 1) {
        pageNumbers.add(page);
      }

      const sortedPages = Array.from(pageNumbers).sort((a, b) => a - b);
      const pageItems = [];
      let previousPage = 0;
      sortedPages.forEach((page) => {
        if (previousPage && page - previousPage > 1) {
          pageItems.push('<li class="hidden md:block"><span class="block leading-none ml-2 mr-2">…</span></li>');
        }
        if (page === currentPage) {
          pageItems.push(`<li class="hidden md:block"><span class="pagination__page-current font-bold block leading-none">${page}</span></li>`);
        } else {
          pageItems.push(`<li class="hidden md:block"><a class="pagination__page-link text-current block leading-none" data-intersection-page href="${this.escapeHtml(this.getIntersectionPageUrl(params, page))}">${page}</a></li>`);
        }
        previousPage = page;
      });

      const previousLink = currentPage > 1
        ? `href="${this.escapeHtml(this.getIntersectionPageUrl(params, currentPage - 1))}" data-intersection-page`
        : 'role="link" aria-disabled="true"';
      const nextLink = currentPage < totalPages
        ? `href="${this.escapeHtml(this.getIntersectionPageUrl(params, currentPage + 1))}" data-intersection-page`
        : 'role="link" aria-disabled="true"';

      return `
        <nav class="filter-tabs__intersection-pagination" aria-label="Pagination">
          <ul class="pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto mb-10">
            <li class="pagination__item pagination__item--arrow">
              <a class="pagination__arrow pagination__arrow--prev hover:lighten flex text-current items-center" ${previousLink}>
                <span>${this.escapeHtml(this.dataset.previousLabel || 'Previous')}</span>
              </a>
            </li>
            <li class="pagination__item text-center md:hidden font-bold">${currentPage} / ${totalPages}</li>
            ${pageItems.join('')}
            <li class="pagination__item pagination__item--arrow">
              <a class="pagination__arrow pagination__arrow--next hover:lighten flex justify-end text-current items-center" ${nextLink}>
                <span>${this.escapeHtml(this.dataset.nextLabel || 'Next')}</span>
              </a>
            </li>
          </ul>
        </nav>`;
    }

    getIntersectionPageUrl(params, page) {
      const pageParams = new URLSearchParams(params);
      if (page > 1) pageParams.set(this.intersectionPageParam, page);
      else pageParams.delete(this.intersectionPageParam);
      return this.buildUrl(pageParams);
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value;
      return element.innerHTML;
    }

    applyNativeFilterVisibility() {
      if (this.dataset.hideNativeFilters === 'true') {
        document.documentElement.classList.add('collection-filter-tabs--hide-native');
      }
    }

    applyClosedGridLayout() {
      if (this.dataset.hideNativeFilters !== 'true') return;

      const productList = document.querySelector('.main-products-grid__results > .grid');
      if (productList?.dataset.filtersClosedClasses) {
        productList.className = productList.dataset.filtersClosedClasses;
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      document.getElementById('filter-results')?.classList.toggle('is-loading', isLoading);

      const status = this.querySelector('[data-filter-status]');
      if (status) status.textContent = isLoading ? 'Updating products…' : 'Products updated.';
    }
  }

  customElements.define('collection-filter-tabs', CollectionFilterTabs);
}
